/**
 * apiMessages — read the human message out of ANY API outcome, and spot the
 * backend's "failed, but HTTP 200" envelope.
 *
 * Why one place: the backend reports failure two ways and words it two ways —
 *  - an HTTP error (RTK Query `error`, carrying `normalized.message` from
 *    services/interceptors.js), or
 *  - an HTTP 200 whose body says `ErrorCode: -1` (some endpoints spell it
 *    `Errorcode` — legacy InstancesListTable.vue:1291 checks that spelling),
 * with the text under `Message` OR `message`. Every caller that shows a toast
 * would otherwise re-implement that guesswork.
 *
 * Pure: no React, no store — usable from hooks, components and tests alike.
 */

export const FALLBACK_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/** The backend's failure code on a 200 response. */
const FAILURE_CODE = -1;

/** The ErrorCode of a response body, whichever spelling it uses. */
function errorCodeOf(body) {
  return body?.ErrorCode ?? body?.Errorcode ?? body?.errorCode;
}

/** The message of a response body or error data, whichever key it uses. */
function messageOf(body) {
  const text = body?.Message ?? body?.message;
  return typeof text === 'string' && text.trim() ? text.trim() : '';
}

/**
 * Is this 200 response body actually a failure? (`ErrorCode: -1`)
 */
export function isFailureEnvelope(body) {
  return Number(errorCodeOf(body)) === FAILURE_CODE;
}

/**
 * The message to show for a failure — from whatever was caught or returned:
 *  - an RTK Query error ({ status, data, normalized }) or `.unwrap()` rejection,
 *  - a thrown Error,
 *  - a failure envelope body ({ ErrorCode: -1, Message }),
 *  - a plain string.
 * Falls back to `fallback` when nothing readable is there.
 */
export function readApiError(error, fallback = FALLBACK_ERROR_MESSAGE) {
  if (!error) return fallback;
  if (typeof error === 'string') return error.trim() || fallback;
  return (
    error.normalized?.message ||
    messageOf(error.data) ||
    messageOf(error) ||
    (error instanceof Error && error.message) ||
    fallback
  );
}

/**
 * An Error for a failure envelope, so callers can `throw` it into the same
 * catch block that handles HTTP errors.
 */
export function envelopeError(body, fallback = FALLBACK_ERROR_MESSAGE) {
  const error = new Error(messageOf(body) || fallback);
  error.data = body;
  return error;
}
